import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  IconButton,
  Snackbar,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { isEqual } from 'lodash';
import { AccountService, WhatsAppSettingsUpdateData } from '../../api';
import { MAX_EXTRA_LINES_CHARS, WhatsAppSettingsTab } from '../types/WhatsAppSettingsTypes';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { useGetAccountAuthenticated } from '../../session/swr/useGetAccountAuthenticated';
import { TabsPage } from '../../common/components/TabsPage';
import { FormTextField } from '../../experienceSettings/formComponents/FormTextField';
import { WhatsAppTemplatePreview } from './WhatsAppTemplatePreview';
import { useLanguage } from '../../common/GeneralUtils';
import { getOrderPage } from '../../utils/AccountUtils';

type EditWhatsAppSettingsProps = {
  orderPageIdOverride?: string;
};

export const EditWhatsAppSettings = ({ orderPageIdOverride }: EditWhatsAppSettingsProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { readOnly } = UseAuthPermissions();
  const { account, updateLocalAccount } = useGetAccountAuthenticated({});
  const { t } = useTranslation();
  const language = useLanguage();
  const posthog = usePostHog();
  const pickupFeatureEnabled = useFeatureFlagEnabled('feature-pickup-ready');

  const isOverrideSettings = !!orderPageIdOverride;

  const [isUpdatedSnackOpen, setIsUpdatedSnackOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isDisabled = readOnly || isSubmitting;

  // Get order page if we have an override
  const orderPage = orderPageIdOverride ? getOrderPage(account, orderPageIdOverride) : null;

  // Use order page settings if available, otherwise fall back to account settings
  const whatsappSettings = orderPage?.whatsapp_settings || account?.whatsapp_settings || {};

  const initialValues: WhatsAppSettingsUpdateData = {
    admin_phones: whatsappSettings.admin_phones || [],
    should_send_customer_confirmations: whatsappSettings.should_send_customer_confirmations || false,
    should_send_manager_confirmations: whatsappSettings.should_send_manager_confirmations || false,
    should_send_reminders: whatsappSettings.should_send_reminders || false,
    confirmation_message_extra_lines: whatsappSettings.confirmation_message_extra_lines || [
      { en: '', he: '' },
      { en: '', he: '' },
    ],
    reminder_message_extra_lines: whatsappSettings.reminder_message_extra_lines || [
      { en: '', he: '' },
      { en: '', he: '' },
    ],
    pickup_message_extra_lines: whatsappSettings.pickup_message_extra_lines || [
      { en: '', he: '' },
      { en: '', he: '' },
      { en: '', he: '' },
    ],
    reminder_pre_workshop_hours: whatsappSettings.reminder_pre_workshop_hours || 24,
    should_send_post_workshop: whatsappSettings.should_send_post_workshop || false,
    post_workshop_message_extra_lines: whatsappSettings.post_workshop_message_extra_lines || [
      { en: '', he: '' },
      { en: '', he: '' },
    ],
    post_workshop_hours: whatsappSettings.post_workshop_hours || 24,
  };

  const calculateTotalChars = (lines: Array<{ en?: string; he?: string }>, lang: string) => {
    return lines.reduce((total, line) => total + (line[lang as keyof typeof line]?.length || 0), 0);
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        enabled: Yup.boolean(),
        admin_phones: Yup.array().of(
          Yup.string().matches(/^\+?[1-9]\d{1,14}$/, t('whatsappSettings.errors.invalidPhoneNumber')),
        ),
        confirmation_message_extra_lines: Yup.array().test(
          'character-limit',
          t('whatsappSettings.errors.tooManyCharacters', { limit: MAX_EXTRA_LINES_CHARS }),
          function validateConfirmationChars(value: any) {
            if (!value) return true;
            const enTotal = calculateTotalChars(value, 'en');
            const heTotal = calculateTotalChars(value, 'he');
            return enTotal <= MAX_EXTRA_LINES_CHARS && heTotal <= MAX_EXTRA_LINES_CHARS;
          },
        ),
        reminder_message_extra_lines: Yup.array().test(
          'character-limit',
          t('whatsappSettings.errors.tooManyCharacters', { limit: MAX_EXTRA_LINES_CHARS }),
          function validateReminderChars(value: any) {
            if (!value) return true;
            const enTotal = calculateTotalChars(value, 'en');
            const heTotal = calculateTotalChars(value, 'he');
            return enTotal <= MAX_EXTRA_LINES_CHARS && heTotal <= MAX_EXTRA_LINES_CHARS;
          },
        ),
        pickup_message_extra_lines: Yup.array().test(
          'character-limit',
          t('whatsappSettings.errors.tooManyCharacters', { limit: MAX_EXTRA_LINES_CHARS }),
          function validatePickupChars(value: any) {
            if (!value) return true;
            const enTotal = calculateTotalChars(value, 'en');
            const heTotal = calculateTotalChars(value, 'he');
            return enTotal <= MAX_EXTRA_LINES_CHARS && heTotal <= MAX_EXTRA_LINES_CHARS;
          },
        ),
        reminder_pre_workshop_hours: Yup.number()
          .min(1, t('whatsappSettings.errors.invalidHours', { min: 1, max: 240 }))
          .max(240, t('whatsappSettings.errors.invalidHours', { min: 1, max: 240 })),
        post_workshop_message_extra_lines: Yup.array().test(
          'character-limit',
          t('whatsappSettings.errors.tooManyCharacters', { limit: MAX_EXTRA_LINES_CHARS }),
          function validatePostWorkshopChars(value: any) {
            if (!value) return true;
            const enTotal = calculateTotalChars(value, 'en');
            const heTotal = calculateTotalChars(value, 'he');
            return enTotal <= MAX_EXTRA_LINES_CHARS && heTotal <= MAX_EXTRA_LINES_CHARS;
          },
        ),
        post_workshop_hours: Yup.number()
          .min(1, t('whatsappSettings.errors.invalidHours', { min: 1, max: 240 }))
          .max(240, t('whatsappSettings.errors.invalidHours', { min: 1, max: 240 })),
      }),
    [t],
  );

  const preprocessMessageLines = (text: string): string => {
    return text.replace(/\s{4,}/g, ' ').replace(/[\t\n]/g, ' ');
  };

  const onSubmit = async (values: WhatsAppSettingsUpdateData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const processedValues = {
        ...values,
        reminder_message_extra_lines: values.reminder_message_extra_lines?.map((line) => ({
          en: line.en ? preprocessMessageLines(line.en) : line.en,
          he: line.he ? preprocessMessageLines(line.he) : line.he,
        })),
        confirmation_message_extra_lines: values.confirmation_message_extra_lines?.map((line) => ({
          en: line.en ? preprocessMessageLines(line.en) : line.en,
          he: line.he ? preprocessMessageLines(line.he) : line.he,
        })),
        pickup_message_extra_lines: values.pickup_message_extra_lines?.map((line) => ({
          en: line.en ? preprocessMessageLines(line.en) : line.en,
          he: line.he ? preprocessMessageLines(line.he) : line.he,
        })),
        post_workshop_message_extra_lines: values.post_workshop_message_extra_lines?.map((line) => ({
          en: line.en ? preprocessMessageLines(line.en) : line.en,
          he: line.he ? preprocessMessageLines(line.he) : line.he,
        })),
      };

      const updatedAccount = await AccountService.updateWhatsappSettings(processedValues);
      await updateLocalAccount(updatedAccount);
      posthog.capture('WhatsApp template updated');
      setIsUpdatedSnackOpen(true);
    } catch (error) {
      console.error('Error saving WhatsApp settings:', error);
      setSubmitError(t('whatsappSettings.errors.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAdminNotificationsTab = (values: WhatsAppSettingsUpdateData, setFieldValue: any) => (
    <Grid container flexDirection="column" gap={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('whatsappSettings.adminNotifications.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('whatsappSettings.adminNotifications.description')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={values.should_send_manager_confirmations || false}
                onChange={(e) => setFieldValue('should_send_manager_confirmations', e.target.checked)}
                disabled={isDisabled}
              />
            }
            label={t('whatsappSettings.adminNotifications.enableNotifications')}
          />
        </CardContent>
      </Card>

      {values.should_send_manager_confirmations && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('whatsappSettings.adminNotifications.phoneNumbers')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('whatsappSettings.adminNotifications.phoneNumbersDescription')}
            </Typography>

            {(values.admin_phones || []).map((phone, index) => (
              <Grid container key={index} sx={{ gap: 1, mb: 2, alignItems: 'flex-start' }}>
                <Grid item>
                  <FormTextField
                    isFieldUpdate={values.admin_phones?.[index] !== initialValues.admin_phones?.[index]}
                    fieldName={`admin_phones.${index}`}
                    label={t('whatsappSettings.adminNotifications.phoneNumber', { number: index + 1 })}
                    placeholder="+1234567890"
                    disabled={isDisabled}
                    width={250}
                  />
                </Grid>
                <IconButton
                  onClick={() => {
                    const newPhones = [...(values.admin_phones || [])];
                    newPhones.splice(index, 1);
                    setFieldValue('admin_phones', newPhones);
                  }}
                  disabled={isDisabled}
                  size="small">
                  <Delete />
                </IconButton>
              </Grid>
            ))}

            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => {
                const newPhones = [...(values.admin_phones || []), ''];
                setFieldValue('admin_phones', newPhones);
              }}
              disabled={isDisabled || (values.admin_phones || []).length >= 2}>
              {t('whatsappSettings.adminNotifications.addPhoneNumber')}
            </Button>

            {(values.admin_phones || []).length >= 2 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('whatsappSettings.adminNotifications.maxPhonesReached')}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Grid>
  );

  const renderExtraLinesSection = (
    values: WhatsAppSettingsUpdateData,
    setFieldValue: any,
    fieldPrefix:
      | 'confirmation_message_extra_lines'
      | 'reminder_message_extra_lines'
      | 'pickup_message_extra_lines'
      | 'post_workshop_message_extra_lines',
    titleKey: string,
    templateType: 'confirmation' | 'reminder' | 'pickup' | 'post_workshop',
  ) => {
    const defaultLines =
      fieldPrefix === 'pickup_message_extra_lines'
        ? [
            { en: '', he: '' },
            { en: '', he: '' },
            { en: '', he: '' },
          ]
        : [
            { en: '', he: '' },
            { en: '', he: '' },
          ];
    const lines = values[fieldPrefix] || defaultLines;
    const languages = account.customer_facing_widgets_languages || ['en'];

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t(titleKey)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('whatsappSettings.extraLines.description')}
          </Typography>

          {languages.map((lang) => (
            <Grid container gap={2} mt={2} key={lang} flexDirection={isSmallScreen ? 'column' : 'row'}>
              <Grid item xs={12}>
                <Typography fontWeight={700}>{t(`emailTemplates.${lang}`)}</Typography>
              </Grid>

              {/* Text fields section */}
              <Grid item xs={12} md={6} container flexDirection="column" gap={1}>
                <Grid item>
                  <FormTextField
                    isFieldUpdate
                    fieldName={`${fieldPrefix}.0.${lang}`}
                    label={t('whatsappSettings.extraLines.line', { number: 1 })}
                    disabled={isDisabled}
                    width={isSmallScreen ? 250 : 400}
                  />
                </Grid>

                <Grid item>
                  <FormTextField
                    isFieldUpdate
                    fieldName={`${fieldPrefix}.1.${lang}`}
                    label={t('whatsappSettings.extraLines.line', { number: 2 })}
                    disabled={isDisabled}
                    width={isSmallScreen ? 250 : 400}
                  />
                </Grid>

                {fieldPrefix === 'pickup_message_extra_lines' && (
                  <Grid item>
                    <FormTextField
                      isFieldUpdate
                      fieldName={`${fieldPrefix}.2.${lang}`}
                      label={t('whatsappSettings.extraLines.line', { number: 3 })}
                      disabled={isDisabled}
                      width={isSmallScreen ? 250 : 400}
                    />
                  </Grid>
                )}

                <Typography variant="caption" color="text.secondary">
                  {t('whatsappSettings.extraLines.characterCount', {
                    count: calculateTotalChars(lines, lang),
                    limit: MAX_EXTRA_LINES_CHARS,
                  })}
                </Typography>
              </Grid>

              {/* Preview section */}
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <WhatsAppTemplatePreview
                  templateType={templateType}
                  language={lang as 'en' | 'he'}
                  templates={whatsappSettings.templates}
                  extraLines={lines}
                />
              </Grid>
            </Grid>
          ))}
        </CardContent>
      </Card>
    );
  };

  const renderCustomerConfirmationsTab = (values: WhatsAppSettingsUpdateData, setFieldValue: any) => (
    <Grid container flexDirection="column" gap={3}>
      <Card>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={values.should_send_customer_confirmations || false}
                onChange={(e) => setFieldValue('should_send_customer_confirmations', e.target.checked)}
                disabled={isDisabled}
              />
            }
            label={t('whatsappSettings.customerConfirmations.enabled')}
          />
        </CardContent>
      </Card>

      {values.should_send_customer_confirmations &&
        renderExtraLinesSection(
          values,
          setFieldValue,
          'confirmation_message_extra_lines',
          'whatsappSettings.customerConfirmations.extraLines',
          'confirmation',
        )}
    </Grid>
  );

  const renderCustomerRemindersTab = (values: WhatsAppSettingsUpdateData, setFieldValue: any) => (
    <Grid container flexDirection="column" gap={3}>
      <Card>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={values.should_send_reminders || false}
                onChange={(e) => setFieldValue('should_send_reminders', e.target.checked)}
                disabled={isDisabled}
              />
            }
            label={t('whatsappSettings.customerReminders.enabled', {
              activityLabel: account?.activity_label_plural?.[language]?.toLowerCase(),
            })}
          />
        </CardContent>
      </Card>

      {values.should_send_reminders && (
        <>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('whatsappSettings.customerReminders.timing')}
              </Typography>
              <FormTextField
                isFieldUpdate={values.reminder_pre_workshop_hours !== initialValues.reminder_pre_workshop_hours}
                fieldName="reminder_pre_workshop_hours"
                label={t('whatsappSettings.customerReminders.hoursBeforeWorkshop')}
                type="number"
                disabled={isDisabled}
                width={150}
                min={1}
                max={240}
              />
            </CardContent>
          </Card>

          {renderExtraLinesSection(
            values,
            setFieldValue,
            'reminder_message_extra_lines',
            'whatsappSettings.customerReminders.extraLines',
            'reminder',
          )}
        </>
      )}
    </Grid>
  );

  const renderPostWorkshopTab = (values: WhatsAppSettingsUpdateData, setFieldValue: any) => (
    <Grid container flexDirection="column" gap={3}>
      <Card>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={values.should_send_post_workshop || false}
                onChange={(e) => setFieldValue('should_send_post_workshop', e.target.checked)}
                disabled={isDisabled}
              />
            }
            label={t('whatsappSettings.postWorkshop.enabled', {
              activityLabel: account?.activity_label_plural?.[language]?.toLowerCase(),
            })}
          />
        </CardContent>
      </Card>

      {values.should_send_post_workshop && (
        <>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('whatsappSettings.postWorkshop.timing')}
              </Typography>
              <FormTextField
                isFieldUpdate={values.post_workshop_hours !== initialValues.post_workshop_hours}
                fieldName="post_workshop_hours"
                label={t('whatsappSettings.postWorkshop.hoursAfterWorkshop')}
                type="number"
                disabled={isDisabled}
                width={150}
                min={1}
                max={240}
              />
            </CardContent>
          </Card>

          {renderExtraLinesSection(
            values,
            setFieldValue,
            'post_workshop_message_extra_lines',
            'whatsappSettings.postWorkshop.extraLines',
            'post_workshop',
          )}
        </>
      )}
    </Grid>
  );

  const renderPickupNotificationsTab = (values: WhatsAppSettingsUpdateData, setFieldValue: any) => (
    <Grid container flexDirection="column" gap={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('whatsappSettings.pickupNotifications.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('whatsappSettings.pickupNotifications.description')}
          </Typography>
        </CardContent>
      </Card>

      {renderExtraLinesSection(
        values,
        setFieldValue,
        'pickup_message_extra_lines',
        'whatsappSettings.pickupNotifications.extraLines',
        'pickup',
      )}
    </Grid>
  );

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ values, handleSubmit, setFieldValue, errors }) => {
        const tabContent = {
          [WhatsAppSettingsTab.CUSTOMER_CONFIRMATIONS]: renderCustomerConfirmationsTab(values, setFieldValue),
          [WhatsAppSettingsTab.CUSTOMER_REMINDERS]: renderCustomerRemindersTab(values, setFieldValue),
          [WhatsAppSettingsTab.POST_WORKSHOP]: renderPostWorkshopTab(values, setFieldValue),
          ...(pickupFeatureEnabled && {
            [WhatsAppSettingsTab.PICKUP_NOTIFICATIONS]: renderPickupNotificationsTab(values, setFieldValue),
          }),
          [WhatsAppSettingsTab.ADMIN_NOTIFICATIONS]: renderAdminNotificationsTab(values, setFieldValue),
        };

        return (
          <Form onSubmit={handleSubmit} style={{ height: '100%' }}>
            <Grid
              sx={{
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}>
              <Snackbar
                open={isUpdatedSnackOpen}
                onClose={() => setIsUpdatedSnackOpen(false)}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert
                  sx={{ width: '100%' }}
                  onClose={() => setIsUpdatedSnackOpen(false)}
                  severity="success"
                  variant="filled">
                  {t('whatsappSettings.successMessage')}
                </Alert>
              </Snackbar>

              {!isOverrideSettings && (
                <Grid container justifyContent="center" sx={{ flexShrink: 0 }}>
                  <Typography variant={isSmallScreen ? 'h5' : 'h4'}>{t('whatsappSettings.title')}</Typography>
                </Grid>
              )}

              <Grid
                container
                flexDirection="column"
                flexWrap="nowrap"
                sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <TabsPage tabContent={tabContent} />
              </Grid>

              {submitError && (
                <Alert severity="error" onClose={() => setSubmitError(null)} sx={{ flexShrink: 0 }}>
                  {submitError}
                </Alert>
              )}

              <Grid container justifyContent="center" sx={{ flexShrink: 0 }}>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (Object.keys(errors).length > 0) {
                      const errorMessages = Object.entries(errors)
                        .map(([field, error]) => `${field}: ${error}`)
                        .join('\n');
                      setSubmitError(t('whatsappSettings.errors.validationFailed', { errors: errorMessages }));
                      return;
                    }
                    handleSubmit();
                  }}
                  disabled={isDisabled || isEqual(values, initialValues)}
                  loading={isSubmitting}
                  size={isSmallScreen ? 'medium' : 'large'}>
                  {t('whatsappSettings.saveSettings')}
                </LoadingButton>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
